<template>
  <div class="device-page">
    <div class="float-right">
      <BackButton :serialNum="product_serial_num" :fromDevicePage="this.$route.params.fromDevicePage"/>
    </div>
    <h1 class="page-title">Device Details</h1>
    <b-tabs>
      <div>
        <div class="device-status">Device {{ product_serial_num }}</div>
        <a class="device-status device-status-border" v-if="deviceOnline()" @click="updateDeviceDetailsLive" v-b-tooltip.hover title="Refresh"><i class="glyphicon glyphicon-refresh"  /></a>
        <div class="device-status device-status-border device-status-font-normal" v-b-tooltip.hover :title="datetimeFormatter(deviceLastMsgTime())">Last communication: <timeago :datetime="deviceLastMsgTime()" :autoUpdate="true"></timeago></div>
        <div class="device-status device-status-border" v-bind:class="{ red: !deviceOnline() }"><span class="dot" v-bind:class="(deviceOnline()) ? 'bg-green' : 'bg-red'"/> {{ this.getOnlineString(deviceOnline()) }}</div>
        <a class="device-status device-status-border reconfigure-link" v-if="activeTab === 'Configuration' && $base.isAllowed('forceCalibrate')" @click="reconfigureClicked" v-b-tooltip.hover title="Reconfigure MLS"><i class="glyphicon glyphicon-cog"></i> Auto re-configure</a>
      </div>
      <div style="clear: both"></div>
      <b-tab title="Info" active lazy @click="getDetails">
        <b-container>
          <b-row>
            <b-card-group deck class="w-100">
              <MeasurementsCard
                v-bind:rms="this.deviceStatus && this.deviceStatus.rms ? this.deviceStatus.rms : undefined"
                v-bind:pole="this.pole ? this.pole : undefined"
              />
              <TemperaturesCard
                v-bind:temperature="this.deviceStatus && this.deviceStatus.temperature ? this.deviceStatus.temperature : undefined"
              />
            </b-card-group>
          </b-row>
          <b-row>
            <b-card-group deck class="w-100">
              <PoleDetailCard v-bind:pole="this.pole ? this.pole : undefined" />
              <GPSLocationCard
                v-bind:gps_location="this.deviceStatus && this.deviceStatus.gps ? this.deviceStatus.gps : undefined"
              />
            </b-card-group>
          </b-row>
          <b-row>
            <b-card-group deck class="w-100">
              <PowerSupplyCard
                v-bind:power_supply="this.deviceStatus && this.deviceStatus.power_supply ? this.deviceStatus.power_supply : undefined"
              />
              <VersionCard
                v-bind:version="this.deviceStatus && this.deviceStatus.version ? this.deviceStatus.version : undefined"
              />
            </b-card-group>
          </b-row>
          <b-row>
            <b-card-group deck class="w-50">
                <GSMCard
                  :gsm="this.deviceStatus && this.deviceStatus.gsm ? this.deviceStatus.gsm : undefined"
                />
            </b-card-group>
          </b-row>
        </b-container>
      </b-tab>
      <b-tab title="Events" @click="eventsTabClicked" lazy>
        <EventsTable />
      </b-tab>
      <b-tab title="Installation History" @click="installationHistoryTabClicked" lazy>
        <InstallationHistoryTab />
      </b-tab>
      <b-tab title="Configuration" @click="configurationTabClicked" v-if="$base.isAllowed('viewDeviceConfiguration')" lazy>
        <DeviceConfigTab :getConfigFailure="this.getConfigFailure" />
      </b-tab>
      <b-tab title="Log Files" @click="logFilesTabClicked" v-if="$base.isAllowed('editDeviceConfiguration')" lazy>
        <LogsTab/>
      </b-tab>
    </b-tabs>
    <b-modal ref="reconfigure-modal" id="reconfigure-modal" title="Reconfigure MLS"
      header-bg-variant="dark" size="lg" header-text-variant="light" ok-only  @ok="sendReconfigureRequest">
      <h3>Are you sure you want to reconfigure device {{ product_serial_num }}</h3>
    </b-modal>
  </div>
</template>



<script>
import { deviceService } from "@/_services";
import { mapActions, mapGetters } from 'vuex';
import BackButton from "@/components/BackButton/BackButton";
import datetimeFormatter from "@/core/datetimeFormatterHelper";
import MeasurementsCard from "@/components/Device/MeasurementsCard/MeasurementsCard";
import TemperaturesCard from "@/components/Device/TemperaturesCard/TemperaturesCard";
import PoleDetailCard from "@/components/Device/PoleDetailCard/PoleDetailCard";
import GPSLocationCard from "@/components/Device/GPSLocationCard/GPSLocationCard";
import PowerSupplyCard from "@/components/Device/PowerSupplyCard/PowerSupplyCard";
import VersionCard from "@/components/Device/VersionCard/VersionCard";
import GSMCard from "@/components/Device/GSMCard/GSMCard";
import EventsTable from "@/components/Device/EventsTable/EventsTable";
import InstallationHistoryTab from "@/components/Device/InstallationHistoryTab/InstallationHistoryTab";
import DeviceConfigTab from "@/components/Device/ConfigTab/ConfigTab";
import LogsTab from "@/components/Device/LogsTab/LogsTab.vue";
export default {
  name: "DeviceDetails",
  components: {
    BackButton,
    MeasurementsCard,
    TemperaturesCard,
    PoleDetailCard,
    GPSLocationCard,
    PowerSupplyCard,
    VersionCard,
    GSMCard,
    EventsTable,
    InstallationHistoryTab,
    DeviceConfigTab,
    LogsTab
  },
  methods: {
    ...mapActions('alert', ['error', 'success']),
    ...mapActions('device', ['saveDevice', 'saveDeviceOnline']),
    ...mapGetters('device', ['deviceOnline', 'deviceLastMsgTime']),
    datetimeFormatter,
    getDetails() {
      this.activeTab = 'Info'
      return deviceService.findDetailsForDevice(this.$route.params.serial)
        .then(response => {
          this.deviceStatus = response.data.status;
          this.pole = response.data.pole;
          this.product_serial_num = response.data.product_serial_num;
          this.saveDevice({online: response.data.online, lastMsgTime: response.data.server_last_message_time});
        })
        .catch(() => {
          this.error('Error getting Device Status');
        });
    },
    sendReconfigureRequest(){
      deviceService.reconfigureDevice(this.product_serial_num, this.pole.uuid).then( () => {
        this.success("Successfully Configured MLS codes on device " + this.product_serial_num);
      })
      .catch(error => {
        this.error('Failed to force MLS code assignment for device ' + this.product_serial_num);
      })
      this.$bvModal.hide('reconfigure-modal');
    },
    configurationTabClicked(){
      this.activeTab = 'Configuration'
    },
    logFilesTabClicked(){
      this.activeTab = 'LogFiles'
    },
    eventsTabClicked(){
      this.activeTab = 'Events'
    },
    installationHistoryTabClicked(){
      this.activeTab = 'InstallationHistory'
    },
    reconfigureClicked(){
      this.$bvModal.show('reconfigure-modal');
    },
    updateDeviceDetailsLive() {
      if (!this.deviceOnline()) {
        return;  // device is offline, no point refreshing data
      }
      deviceService.getDeviceStatusLive(this.$route.params.serial)
      .then(response => {
        this.deviceStatus = response.data.status;
        this.saveDevice({online: true, lastMsgTime: response.data.server_last_message_time});
      })
      .catch(error => {
        if (error.response.status == 504) {
          this.saveDeviceOnline(false);
        }
        else {
          this.error('Error getting Device Status Live');
        }
      });
    },
    getOnlineString(value) {
      if(value) {
        return "ONLINE"
      } else {
        return "OFFLINE"
      }
    }
  },
  data() {
    return {
      getConfigFailure: false,
      deviceStatus: {},
      pole: {},
      product_serial_num: "",
      activeTab: ''
    };
  },
  mounted() {
    this.getDetails()
    .then(() => {
      this.updateDeviceDetailsLive();
    })
    .catch(() => {});
  }
};
</script>

<style src="./Device.scss" lang="scss" />
